import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { InlineLoadingState } from '../../LoadingSpinner';
import { ENTRY_TYPE_COLORS, ENTRY_TYPE_LABEL, type EmployeeTimeEntryType } from '../employeeTimeEntryConstants';
import { isHourTrackingEntryType, toHm } from '../../../utils/employeeTimeEntryHours';
import {
  groupTimeEntryChangeRequests,
  type GroupableChangeRequest,
} from '../../../utils/groupTimeEntryChangeRequests';

type ChangeRequest = GroupableChangeRequest;

function entryTypeOf(payload: Record<string, unknown> | null): string {
  return String(payload?.entry_type ?? '').trim();
}

function formatEntrySummary(payload: Record<string, unknown> | null): string {
  if (!payload) return '-';
  const type = entryTypeOf(payload);
  const label = (ENTRY_TYPE_LABEL[type as EmployeeTimeEntryType] ?? type) || '-';
  const note = String(payload.note ?? '').trim();
  let summary = label;
  if (isHourTrackingEntryType(type)) {
    const start = toHm(String(payload.start_time ?? ''));
    const end = toHm(String(payload.end_time ?? ''));
    if (start && end) summary = `${label} ${start}–${end}`;
  }
  return note ? `${summary} · ${note}` : summary;
}

function formatGermanDate(ymd: string, withYear = true): string {
  const [y, m, d] = ymd.split('-').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return ymd;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: 'numeric' as const } : {}),
    timeZone: 'UTC',
  });
}

function formatGermanDateRange(fromYmd: string, toYmd: string): string {
  if (fromYmd === toYmd) return formatGermanDate(fromYmd);
  const fromYear = fromYmd.slice(0, 4);
  const toYear = toYmd.slice(0, 4);
  const fromLabel = formatGermanDate(fromYmd, fromYear !== toYear);
  return `${fromLabel} – ${formatGermanDate(toYmd)}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

function TypeBadge({ type }: { type: string }) {
  const label = ENTRY_TYPE_LABEL[type as EmployeeTimeEntryType] ?? type;
  const color = ENTRY_TYPE_COLORS[type as EmployeeTimeEntryType] ?? 'bg-app-surface-muted text-app-text-muted';
  if (!type) return null;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

async function postChangeRequestAction(
  id: string,
  action: 'approve' | 'reject',
  reviewNote?: string,
): Promise<{ id: string; ok: boolean; error?: string }> {
  const res = await fetch(`/api/employees/time-entry-change-requests/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    credentials: 'include',
    ...(action === 'reject'
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ review_note: reviewNote ?? '' }) }
      : {}),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) return { id, ok: false, error: data.error || `Antrag ${id} fehlgeschlagen.` };
  return { id, ok: true };
}

export function EmployeeOverviewRequestsTab() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<ChangeRequest[]>([]);
  const [isPayrollApprover, setIsPayrollApprover] = React.useState(false);
  const [actingKey, setActingKey] = React.useState<string | null>(null);

  const groups = React.useMemo(() => groupTimeEntryChangeRequests(items), [items]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reqRes, meRes] = await Promise.all([
        fetch('/api/employees/time-entry-change-requests?status=pending', { credentials: 'include' }),
        fetch('/api/me', { credentials: 'include' }),
      ]);
      const reqJson = (await reqRes.json()) as { items?: ChangeRequest[]; error?: string };
      if (!reqRes.ok) throw new Error(reqJson.error || 'Anträge konnten nicht geladen werden.');
      setItems(Array.isArray(reqJson.items) ? reqJson.items : []);
      if (meRes.ok) {
        const me = (await meRes.json()) as { is_payroll_approver?: boolean };
        setIsPayrollApprover(Boolean(me.is_payroll_approver));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Anträge konnten nicht geladen werden.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const applyResults = (ids: string[], results: Array<{ id: string; ok: boolean; error?: string }>, verb: string) => {
    const succeeded = new Set(results.filter((r) => r.ok).map((r) => r.id));
    const failed = results.filter((r) => !r.ok);
    setItems((prev) => prev.filter((item) => !succeeded.has(item.id)));
    if (failed.length === 0) {
      setError(null);
      return;
    }
    if (failed.length === ids.length) {
      setError(failed[0]?.error || `Anträge konnten nicht ${verb} werden.`);
      return;
    }
    setError(`${failed.length} von ${ids.length} Tagen konnten nicht ${verb} werden.`);
  };

  const handleApprove = async (ids: string[]) => {
    setActingKey(ids[0] ?? null);
    setError(null);
    try {
      const results = await Promise.all(ids.map((id) => postChangeRequestAction(id, 'approve')));
      applyResults(ids, results, 'genehmigt');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Antrag konnte nicht genehmigt werden.');
    } finally {
      setActingKey(null);
    }
  };

  const handleReject = async (ids: string[]) => {
    const note = window.prompt(
      ids.length > 1
        ? `Optionaler Ablehnungsgrund (gilt für ${ids.length} Tage):`
        : 'Optionaler Ablehnungsgrund:',
    );
    if (note === null) return;
    setActingKey(ids[0] ?? null);
    setError(null);
    try {
      const results = await Promise.all(ids.map((id) => postChangeRequestAction(id, 'reject', note)));
      applyResults(ids, results, 'abgelehnt');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Antrag konnte nicht abgelehnt werden.');
    } finally {
      setActingKey(null);
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-app-bg">
      <div className="shrink-0 px-5 py-4 border-b border-app-border/80 bg-app-surface">
        <h2 className="text-sm font-semibold text-app-text">Anträge</h2>
        <p className="text-xs text-app-text-subtle mt-1">
          Änderungsanträge für Zeiteinträge - inklusive Stempelkorrekturen, Urlaub und Krankheit aus der App.
        </p>
      </div>

      {error ? (
        <div className="shrink-0 mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {loading ? (
        <InlineLoadingState label="Lade Anträge…" className="flex-1 py-12" />
      ) : items.length === 0 ? (
        <div className="flex flex-1 items-start justify-center p-10">
          <div className="max-w-md text-center mt-12">
            <div className="w-14 h-14 rounded-xl bg-blue-50 text-blue-600 grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-semibold text-app-text mb-2">Keine offenen Anträge</h3>
            <p className="text-sm text-app-text-subtle leading-relaxed">
              Hier erscheinen Anträge für Schichtzeiten, Urlaub und Krankheit - auch aus der Field-App.
            </p>
          </div>
        </div>
      ) : (
        <div className="app-scrollbar flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          <div className="bg-app-surface border border-app-border rounded-lg overflow-hidden">
            <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Mitarbeiter
              </th>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Datum
              </th>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Änderung
              </th>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Antragsteller
              </th>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Eingereicht
              </th>
              {isPayrollApprover ? (
                <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-right text-xs font-medium text-app-text-muted">
                  Aktion
                </th>
              ) : null}
            </tr>
          </thead>
              <tbody>
                {groups.map((group) => {
                  const item = group.items[0];
                  const proposedType = entryTypeOf(item.proposed_payload);
                  const before = formatEntrySummary(item.previous_snapshot);
                  const after = item.action === 'delete' ? 'Löschen' : formatEntrySummary(item.proposed_payload);
                  const hasPrevious = group.items.some((row) => row.previous_snapshot);
                  const showDiff =
                    item.action === 'delete' ||
                    hasPrevious ||
                    isHourTrackingEntryType(proposedType);
                  const busy = actingKey === group.ids[0];
                  const submittedAt = group.items.reduce(
                    (earliest, row) => (row.created_at < earliest ? row.created_at : earliest),
                    item.created_at,
                  );
                  return (
                    <tr key={group.ids.join(',')} className="border-b border-app-border hover:bg-app-hover/80">
                      <td className="px-3 py-3 font-medium text-app-text">{item.employee_name}</td>
                      <td className="px-3 py-3 text-app-text-muted">
                        <div>{formatGermanDateRange(group.fromDate, group.toDate)}</div>
                        {group.dayCount > 1 ? (
                          <div className="text-xs text-app-text-subtle mt-0.5">{group.dayCount} Tage</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-app-text-muted">
                        {item.activity_title ? (
                          <div className="text-xs text-app-text-subtle mb-1">Termin: {item.activity_title}</div>
                        ) : item.lead_title ? (
                          <div className="text-xs text-app-text-subtle mb-1">Projekt: {item.lead_title}</div>
                        ) : item.proposed_payload?.without_activity === true ? (
                          <div className="text-xs text-app-text-subtle mb-1">Ohne Termin</div>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-1.5">
                          {!showDiff && item.action !== 'delete' ? <TypeBadge type={proposedType} /> : null}
                          {showDiff ? (
                            <>
                              <span className="text-app-text-subtle">{before}</span>
                              <span className="text-app-text-subtle">→</span>
                              <span className="font-medium text-app-text">{after}</span>
                            </>
                          ) : String(item.proposed_payload?.note ?? '').trim() ? (
                            <span className="text-app-text-muted">
                              · {String(item.proposed_payload?.note ?? '').trim()}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-app-text-muted">{item.requester_name}</td>
                      <td className="px-3 py-3 text-app-text-muted">{formatDateTime(submittedAt)}</td>
                      {isPayrollApprover ? (
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => { void handleApprove(group.ids); }}
                              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Genehmigen
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => { void handleReject(group.ids); }}
                              className="inline-flex items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 py-1 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                              Ablehnen
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
